<script setup>
import { defineProps, computed } from 'vue'
import DOMPurify from 'dompurify'
import ActionButton from './blocks/ActionButton.vue'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({ blocks: [] })
  },
  darkMode: {
    type: Boolean,
    default: false
  }
})

const sanitize = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'a', 'code', 'mark', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
  })
}

const blocks = computed(() => {
  if (!props.data || !props.data.blocks) {
    return []
  }
  return props.data.blocks
})

const getHeaderTag = (level) => {
  const tags = {
    1: 'h1',
    2: 'h2',
    3: 'h3',
    4: 'h4',
    5: 'h5',
    6: 'h6'
  }
  return tags[level] || 'h2'
}

const getImageClasses = (data) => {
  const classes = ['editor-image']
  if (data.stretched) classes.push('stretched')
  if (data.withBorder) classes.push('with-border')
  if (data.withBackground) classes.push('with-background')
  return classes.join(' ')
}

// Handle both old string format and new object format for list items
const getListItemContent = (item) => {
  if (typeof item === 'string') return item
  if (item && typeof item === 'object' && 'content' in item) return item.content
  return ''
}

const getListItemChildren = (item) => {
  if (item && typeof item === 'object' && Array.isArray(item.items) && item.items.length > 0) {
    return item.items
  }
  return []
}
</script>

<template>
  <div class="editorjs-renderer" :class="{ 'dark-mode': darkMode }">
    <template v-for="(block, index) in blocks" :key="index">
      <!-- Paragraph -->
      <p
        v-if="block.type === 'paragraph'"
        class="editor-paragraph"
        v-html="sanitize(block.data.text)"
      ></p>

      <!-- Header -->
      <component
        v-else-if="block.type === 'header'"
        :is="getHeaderTag(block.data.level)"
        class="editor-header"
        v-html="sanitize(block.data.text)"
      ></component>

      <!-- List (supports nested items from @editorjs/list v2+) -->
      <component
        v-else-if="block.type === 'list'"
        :is="block.data.style === 'ordered' ? 'ol' : 'ul'"
        :class="['editor-list', `editor-list--${block.data.style || 'unordered'}`]"
      >
        <li v-for="(item, itemIndex) in block.data.items" :key="itemIndex">
          <span v-html="sanitize(getListItemContent(item))"></span>
          <!-- Nested list (level 2) -->
          <component
            v-if="getListItemChildren(item).length > 0"
            :is="block.data.style === 'ordered' ? 'ol' : 'ul'"
            :class="['editor-list', `editor-list--${block.data.style || 'unordered'}`, 'editor-list--nested']"
          >
            <li v-for="(child, childIndex) in getListItemChildren(item)" :key="childIndex">
              <span v-html="sanitize(getListItemContent(child))"></span>
              <!-- Nested list (level 3) -->
              <component
                v-if="getListItemChildren(child).length > 0"
                :is="block.data.style === 'ordered' ? 'ol' : 'ul'"
                :class="['editor-list', `editor-list--${block.data.style || 'unordered'}`, 'editor-list--nested']"
              >
                <li v-for="(grandchild, grandchildIndex) in getListItemChildren(child)" :key="grandchildIndex">
                  <span v-html="sanitize(getListItemContent(grandchild))"></span>
                </li>
              </component>
            </li>
          </component>
        </li>
      </component>

      <!-- Image -->
      <figure
        v-else-if="block.type === 'image'"
        :class="getImageClasses(block.data)"
      >
        <img
          :src="block.data.file?.url || block.data.url"
          :alt="block.data.caption || ''"
        />
        <figcaption
          v-if="block.data.caption"
          v-html="sanitize(block.data.caption)"
        ></figcaption>
      </figure>

      <!-- Embed -->
      <div
        v-else-if="block.type === 'embed'"
        class="editor-embed"
      >
        <iframe
          :src="block.data.embed"
          :width="block.data.width || '100%'"
          :height="block.data.height || 320"
          frameborder="0"
          allowfullscreen
        ></iframe>
        <figcaption
          v-if="block.data.caption"
          v-html="sanitize(block.data.caption)"
        ></figcaption>
      </div>

      <!-- Code -->
      <pre
        v-else-if="block.type === 'code'"
        class="editor-code"
      ><code>{{ block.data.code }}</code></pre>

      <!-- Quote -->
      <blockquote
        v-else-if="block.type === 'quote'"
        class="editor-quote"
        :class="{ 'align-center': block.data.alignment === 'center' }"
      >
        <p v-html="sanitize(block.data.text)"></p>
        <cite
          v-if="block.data.caption"
          v-html="sanitize(block.data.caption)"
        ></cite>
      </blockquote>

      <!-- Table -->
      <table
        v-else-if="block.type === 'table'"
        class="editor-table"
      >
        <tbody>
          <tr v-for="(row, rowIndex) in block.data.content" :key="rowIndex">
            <td
              v-for="(cell, cellIndex) in row"
              :key="cellIndex"
              v-html="sanitize(cell)"
            ></td>
          </tr>
        </tbody>
      </table>

      <!-- Delimiter -->
      <hr
        v-else-if="block.type === 'delimiter'"
        class="editor-delimiter"
      />

      <!-- Action Button -->
      <ActionButton
        v-else-if="block.type === 'actionButton'"
        :url="block.data.url"
        :text="block.data.text"
        :icon="block.data.icon"
        :variant="block.data.variant"
        :badge="block.data.badge"
      />
    </template>
  </div>
</template>

<style scoped>
.editorjs-renderer {
  line-height: 1.6;
  color: #333;
  overflow-wrap: break-word;
}

/* Paragraph */
.editor-paragraph {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  min-height: 1.6em;
}

/* Headers */
.editor-header {
  margin: 1.5rem 0 1rem 0;
  font-weight: 600;
  line-height: 1.3;
}

h1.editor-header { font-size: 2rem; }
h2.editor-header { font-size: 1.75rem; }
h3.editor-header { font-size: 1.5rem; }
h4.editor-header { font-size: 1.25rem; }
h5.editor-header { font-size: 1.1rem; }
h6.editor-header { font-size: 1rem; }

/* Lists */
.editor-list {
  margin: 0 0 1rem 0;
  padding-left: 2rem;
}

.editor-list li {
  margin-bottom: 0.5rem;
}

.editor-list--unordered {
  list-style-type: disc;
}

.editor-list--ordered {
  list-style-type: decimal;
}

.editor-list--nested {
  margin-top: 0.5rem;
  margin-bottom: 0;
}

/* Image */
.editor-image {
  margin: 1.5rem 0;
  text-align: center;
}

.editor-image img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.editor-image.stretched img {
  width: 100%;
}

.editor-image.with-border img {
  border: 1px solid #e0e0e0;
}

.editor-image.with-background {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.editor-image figcaption {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #666;
  font-style: italic;
}

/* Embed */
.editor-embed {
  position: relative;
  margin: 1.5rem 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  border-radius: 8px;
  background-color: #000;
}

.editor-embed iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.editor-embed figcaption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.5rem;
  background-color: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 0.875rem;
  text-align: center;
}

/* Code */
.editor-code {
  margin: 1rem 0;
  padding: 1rem;
  background-color: #2d2d2d;
  border-radius: 8px;
  overflow-x: auto;
}

.editor-code code {
  color: #f8f8f2;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Quote */
.editor-quote {
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  border-left: 4px solid #0d6efd;
  background-color: #f8f9fa;
  font-style: italic;
}

.editor-quote.align-center {
  border-left: none;
  border-top: 4px solid #0d6efd;
  text-align: center;
}

.editor-quote p {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: #333;
}

.editor-quote cite {
  display: block;
  font-size: 0.875rem;
  color: #666;
  font-style: normal;
}

.editor-quote cite::before {
  content: '— ';
}

/* Table */
.editor-table {
  width: 100%;
  margin: 1rem 0;
  border-collapse: collapse;
  border: 1px solid #e0e0e0;
}

.editor-table td {
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  vertical-align: top;
}

.editor-table tr:nth-child(even) {
  background-color: #f8f9fa;
}

/* Delimiter */
.editor-delimiter {
  margin: 2rem auto;
  width: 50%;
  border: none;
  border-top: 2px solid #e0e0e0;
}

/* Dark mode styles */
.dark-mode {
  color: #e5e7eb;
}

.dark-mode .editor-paragraph {
  color: #e5e7eb;
}

.dark-mode .editor-header {
  color: #f3f4f6;
}

.dark-mode .editor-list {
  color: #e5e7eb;
}

.dark-mode .editor-code {
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.dark-mode .editor-code code {
  color: #e5e7eb;
}

.dark-mode .editor-quote {
  background-color: rgba(255, 255, 255, 0.05);
  border-left-color: #7357D2;
}

.dark-mode .editor-quote p {
  color: #e5e7eb;
}

.dark-mode .editor-quote cite {
  color: #9ca3af;
}

.dark-mode .editor-table {
  border-color: rgba(255, 255, 255, 0.1);
}

.dark-mode .editor-table td {
  border-color: rgba(255, 255, 255, 0.1);
  color: #e5e7eb;
}

.dark-mode .editor-table tr:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.05);
}

.dark-mode .editor-delimiter {
  border-top-color: rgba(255, 255, 255, 0.1);
}

.dark-mode .editor-image figcaption {
  color: #9ca3af;
}

.dark-mode .editor-image.with-background {
  background-color: rgba(255, 255, 255, 0.05);
}

.dark-mode .editor-image.with-border img {
  border-color: rgba(255, 255, 255, 0.1);
}

.dark-mode :deep(a:not(.action-button)) {
  color: #7357D2;
}

.dark-mode :deep(a:not(.action-button):hover) {
  color: #8b72e6;
}
</style>
